"use client";

import { useEffect, useRef, type ComponentProps } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames,
  type DayButtonProps,
} from "react-day-picker";

import { fmtYmd } from "./use-popover-anchor";

/** The ghost control the nav buttons and day buttons share. Its type size and
 *  corner come from the calendar's own variables, so a day button follows the
 *  calendar's `size` without being told it. */
const GHOST =
  "inline-flex items-center justify-center rounded-(--day-radius) text-(length:--day-text) font-normal whitespace-nowrap outline-none transition-colors select-none hover:bg-fg/10 hover:text-fg disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0";

/**
 * The office calendar grid: react-day-picker wearing the shadcn calendar's
 * class set on the house tokens — a month caption between chevrons, muted
 * weekday letters, square day cells, the selected day filled and inset so
 * neighbouring fills stay apart, a range shaded between its filled ends, today on a tinted ground. Styled entirely by
 * class, so the library's base stylesheet isn't loaded. Weeks start Monday;
 * a month shows its own days only.
 * The pickers ({@link DatePicker}, {@link DateRangePicker}) wrap it in a
 * popover; use it bare where a month belongs on the page.
 *
 * `size="nano"` is the month as a READING, not a control: a fraction of the
 * cell, mono numerals, no ground or padding of its own (the card it sits in
 * has both), no chevrons. It shows where a day falls — the timestamp's card —
 * so pass it `month` and `selected` and keep it inert. The marked day is the
 * one thing lit: the other days sit back in grey, and its weekday letter
 * lights with it, so the eye lands on the day and reads its column.
 */
export function Calendar({
  className = "",
  classNames,
  showOutsideDays = false,
  size = "default",
  ...props
}: ComponentProps<typeof DayPicker> & { size?: "default" | "nano" }) {
  const d = getDefaultClassNames();
  const nano = size === "nano";
  // The weekday a nano calendar lights: the selected day's, or a range's start.
  const selected = (props as { selected?: unknown }).selected;
  const marked =
    selected instanceof Date
      ? selected
      : selected &&
          typeof selected === "object" &&
          "from" in selected &&
          selected.from instanceof Date
        ? selected.from
        : null;
  const markedWeekday =
    nano && marked
      ? marked.toLocaleDateString("en-US", { weekday: "long" })
      : null;
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={1}
      hideNavigation={nano || undefined}
      className={`group/calendar ${
        nano
          ? "[--cell-size:1.125rem] [--day-radius:0.125rem] [--day-text:9px]"
          : "bg-bg p-2 [--cell-size:2rem] [--day-radius:0.375rem] [--day-text:0.875rem]"
      } ${className}`}
      classNames={{
        root: `w-fit ${d.root}`,
        months: `relative flex flex-col gap-4 md:flex-row ${d.months}`,
        month: `flex w-full flex-col ${nano ? "gap-1" : "gap-4"} ${d.month}`,
        nav: `absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 ${d.nav}`,
        button_previous: `${GHOST} size-(--cell-size) p-0 aria-disabled:opacity-50 ${d.button_previous}`,
        button_next: `${GHOST} size-(--cell-size) p-0 aria-disabled:opacity-50 ${d.button_next}`,
        month_caption: `flex h-(--cell-size) w-full items-center ${
          nano ? "justify-start px-0.5" : "justify-center px-(--cell-size)"
        } ${d.month_caption}`,
        caption_label: `select-none ${
          nano
            ? "font-mono text-[10px] tracking-[0.04em] text-fg"
            : "text-sm font-medium"
        } ${d.caption_label}`,
        month_grid: `w-full border-collapse ${d.month_grid}`,
        weekdays: `flex ${d.weekdays}`,
        weekday: `flex-1 rounded-md font-normal select-none ${
          nano
            ? "font-mono text-[9px] text-fg-subtle"
            : "text-[0.8rem] text-fg-muted"
        } ${d.weekday}`,
        week: `${nano ? "" : "mt-2"} flex w-full ${d.week}`,
        day: `group/day relative isolate aspect-square h-full w-full ${
          nano ? "p-px font-mono text-[9px] text-fg-subtle" : "p-0.5"
        } text-center select-none ${d.day}`,
        range_start: d.range_start,
        range_middle: d.range_middle,
        range_end: d.range_end,
        today: `${nano ? "rounded-sm" : "rounded-md"} bg-fg/10 text-fg data-[selected=true]:bg-transparent ${d.today}`,
        outside: `text-fg-muted aria-selected:text-fg-muted ${d.outside}`,
        disabled: `text-fg-muted opacity-50 ${d.disabled}`,
        hidden: `invisible ${d.hidden}`,
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: c = "" }) => {
          const cls = `size-4 ${c}`;
          if (orientation === "left")
            return <ChevronLeftIcon className={cls} />;
          if (orientation === "right")
            return <ChevronRightIcon className={cls} />;
          return <ChevronDownIcon className={cls} />;
        },
        DayButton: CalendarDayButton,
        // The library names each header cell by its full weekday, which is how
        // the marked one is told apart.
        ...(markedWeekday && {
          Weekday: ({ className: c = "", ...th }) => (
            <th
              {...th}
              className={`${c} ${th["aria-label"] === markedWeekday ? "!text-fg" : ""}`}
            />
          ),
        }),
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className = "",
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const d = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  const single =
    modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle;
  // The band behind a range: drawn per day and bridging the cell padding so
  // it runs unbroken, while the filled ends stay inset from their neighbours.
  const band =
    modifiers.range_start && modifiers.range_end
      ? "none"
      : modifiers.range_start
        ? "start"
        : modifiers.range_end
          ? "end"
          : modifiers.range_middle
            ? "middle"
            : "none";
  return (
    <button
      ref={ref}
      type="button"
      // `YYYY-MM-DD`, not a locale rendering: the server and the browser
      // must write the same attribute or hydration reports a mismatch.
      data-day={fmtYmd(day.date)}
      data-selected-single={single}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-band={band}
      className={`${GHOST} relative flex aspect-square size-auto w-full min-w-[calc(var(--cell-size)-0.25rem)] flex-col gap-1 border-0 leading-none font-normal group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-fg/30 before:pointer-events-none before:absolute before:inset-y-0 before:-z-10 before:hidden before:bg-fg/10 data-[band=middle]:before:-inset-x-0.5 data-[band=middle]:before:block data-[band=start]:before:-right-0.5 data-[band=start]:before:left-0 data-[band=start]:before:block data-[band=start]:before:rounded-l-md data-[band=end]:before:-left-0.5 data-[band=end]:before:right-0 data-[band=end]:before:block data-[band=end]:before:rounded-r-md data-[range-end=true]:bg-fg data-[range-end=true]:text-bg data-[range-end=true]:hover:bg-fg data-[range-middle=true]:text-fg data-[range-start=true]:bg-fg data-[range-start=true]:text-bg data-[range-start=true]:hover:bg-fg data-[selected-single=true]:bg-fg data-[selected-single=true]:text-bg data-[selected-single=true]:hover:bg-fg [&>span]:text-xs [&>span]:opacity-70 ${d.day} ${className}`}
      {...props}
    />
  );
}
